import { useState, useEffect } from "react";
import { useAuth } from "@/context/AuthContext";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Receipt,
  Download,
  Calendar,
  CreditCard,
  CheckCircle,
  XCircle,
  Clock,
  Bell,
  BellOff,
  Loader2,
  RefreshCw
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InvoiceHistoryPage = () => {
  const { getAuthHeader } = useAuth();
  const [invoices, setInvoices] = useState([]);
  const [billing, setBilling] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [invoicesRes, billingRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/billing/invoices`, { headers: getAuthHeader() }),
        axios.get(`${API}/billing/current`, { headers: getAuthHeader() }),
        axios.get(`${API}/billing/notifications`, { headers: getAuthHeader() })
      ]);
      setInvoices(invoicesRes.data.invoices || []);
      setBilling(billingRes.data);
      setNotifications(notificationsRes.data.notifications || []);
    } catch (error) {
      toast.error("Failed to load billing data");
    } finally {
      setLoading(false);
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.post(`${API}/billing/notifications/${notificationId}/read`, {}, {
        headers: getAuthHeader()
      });
      setNotifications(prev => prev.filter(n => n.id !== notificationId));
      toast.success("Notification dismissed");
    } catch (error) {
      toast.error("Failed to dismiss notification");
    }
  };

  const markAllRead = async () => {
    try {
      await axios.post(`${API}/billing/notifications/read-all`, {}, {
        headers: getAuthHeader()
      });
      setNotifications([]);
      toast.success("All notifications dismissed");
    } catch (error) {
      toast.error("Failed to dismiss notifications");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "paid":
        return (
          <Badge className="bg-emerald-500/10 text-emerald-500 border-emerald-500/20 gap-1">
            <CheckCircle className="w-3 h-3" />
            Paid
          </Badge>
        );
      case "pending":
        return (
          <Badge className="bg-amber-500/10 text-amber-500 border-amber-500/20 gap-1">
            <Clock className="w-3 h-3" />
            Pending
          </Badge>
        );
      case "failed":
        return (
          <Badge className="bg-red-500/10 text-red-500 border-red-500/20 gap-1">
            <XCircle className="w-3 h-3" />
            Failed
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="gap-1">
            {status}
          </Badge>
        );
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case "payment_success":
        return <CheckCircle className="w-4 h-4 text-emerald-500" />;
      case "payment_failed":
        return <XCircle className="w-4 h-4 text-red-500" />;
      case "renewal_reminder":
        return <Calendar className="w-4 h-4 text-amber-500" />;
      default:
        return <Bell className="w-4 h-4 text-primary" />;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric"
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return "—";
    return new Date(dateString).toLocaleString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="invoice-history-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl md:text-3xl font-mono font-bold tracking-tight flex items-center gap-3">
            <Receipt className="w-7 h-7 text-primary" />
            Billing & Invoices
          </h1>
          <p className="text-muted-foreground text-sm mt-1">
            View your payment history and billing notifications
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} className="gap-2">
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Notifications */}
      {notifications.length > 0 && (
        <Card className="bg-primary/5 border-primary/20" data-testid="billing-notifications">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-medium flex items-center gap-2">
                <Bell className="w-4 h-4" />
                Billing Notifications
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={markAllRead} className="gap-1 text-xs">
                <BellOff className="w-3 h-3" />
                Dismiss All
              </Button>
            </div>
          </CardHeader>
          <CardContent className="space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className="flex items-center justify-between p-3 bg-background rounded border border-border"
              >
                <div className="flex items-center gap-3">
                  {getNotificationIcon(notification.type)}
                  <div>
                    <p className="text-sm font-medium">{notification.message}</p>
                    <p className="text-xs text-muted-foreground">
                      {formatDateTime(notification.created_at)}
                    </p>
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => markNotificationRead(notification.id)}
                >
                  Dismiss
                </Button>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Current Billing Summary */}
      {billing && (
        <Card className="bg-card/50 border-border" data-testid="billing-summary">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Current Subscription</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div className="p-4 bg-background rounded border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Plan</p>
                <p className="text-lg font-semibold capitalize mt-1">{billing.current_tier}</p>
              </div>
              <div className="p-4 bg-background rounded border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Monthly Price</p>
                <p className="text-lg font-semibold mt-1">
                  {billing.price > 0 ? `$${billing.price.toFixed(2)}` : "Free"}
                </p>
              </div>
              <div className="p-4 bg-background rounded border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Last Payment</p>
                <p className="text-lg font-semibold mt-1">
                  {formatDate(billing.subscription_updated_at)}
                </p>
              </div>
              <div className="p-4 bg-background rounded border border-border">
                <p className="text-xs text-muted-foreground uppercase tracking-wider">Next Billing</p>
                <p className="text-lg font-semibold mt-1">
                  {billing.next_billing_date ? formatDate(billing.next_billing_date) : "—"}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Invoice History Table */}
      <Card className="bg-card/50 border-border" data-testid="invoice-table">
        <CardHeader>
          <CardTitle className="text-sm font-medium flex items-center gap-2">
            <CreditCard className="w-4 h-4" />
            Payment History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border">
                    <TableHead>Date</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead className="text-right">Invoice ID</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {invoices.map((invoice) => (
                    <TableRow key={invoice.id} className="border-border">
                      <TableCell className="font-medium">
                        {formatDate(invoice.created_at)}
                      </TableCell>
                      <TableCell>{invoice.description}</TableCell>
                      <TableCell className="font-mono">
                        ${invoice.amount?.toFixed(2)} {invoice.currency}
                      </TableCell>
                      <TableCell>{getStatusBadge(invoice.status)}</TableCell>
                      <TableCell className="text-right">
                        <code className="text-xs bg-muted px-2 py-1 rounded">
                          {invoice.id?.slice(0, 12)}...
                        </code>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          ) : (
            <div className="text-center py-12 text-muted-foreground">
              <Receipt className="w-12 h-12 mx-auto mb-3 opacity-50" />
              <p>No payment history yet</p>
              <p className="text-sm mt-1">Your invoices will appear here after your first payment</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default InvoiceHistoryPage;
